import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cogoToast from 'cogo-toast';

// Components
import { Alert } from 'wg-fe-ui';
import LicensePlateLookup from '../../../../../../common/src/components/LicensePlateLookup';

// Types
import { Car } from '../../../../../../../types/SMT';
import { VehicleResponseProps } from '../../../../../../../types/ToolkitDashboard';
import { getBrandNameFilter } from '../../../../../../sales-conversation/src/services/productFactoryService';
import { request } from '../../../../../../common/src/legacy/services/httpSessionService';
import { Box, List } from '@wegroup/design-system';
import AdvisoryFlowMainContainer from '../../../ui/advisory-flow/components/app-shell/AdvisoryFlowMainContainer';
import QuestionGroupAssistantQuestion from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupAssistantQuestion';
import QuestionGroupActions from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupActions';
import QuestionGroupBody from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupBody';
import useAuth from '../../../../../../app/src/features/authentication/hooks/useAuth';

interface Props {
  nextSubView: (arg0: string) => void;
  handleChange: (arg0: Car) => void;
  previousSubView: () => void;
  initialLicensePlate?: string;
}

const LicensePlateSearch: React.FC<React.PropsWithChildren<Props>> = ({
  handleChange,
  nextSubView,
  previousSubView,
  initialLicensePlate,
}) => {
  const { t } = useTranslation();
  const [isLicensePlateSupported, setIsLicensePlateSupported] =
    useState<boolean>(true);
  const [brandNameFilter, setBrandNameFilter] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { campaign } = useAuth();

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      if (campaign) {
        const filter = await getBrandNameFilter(campaign);
        setBrandNameFilter(filter || []);
      }
      setIsLoading(false);
    })();
  }, []);

  const mapResponse = (car: Car, images: string[]) => {
    return { ...car, images: images };
  };

  const handleOnChange = async (vehicle: VehicleResponseProps[]) => {
    if (
      vehicle[0].car.brand &&
      brandNameFilter.length > 0 &&
      !brandNameFilter
        .map((name) => name.toLowerCase())
        .includes(vehicle[0].car.brand.toLowerCase())
    ) {
      if (!brandNameFilter.includes(vehicle[0].car.brand.toLowerCase()))
        cogoToast.error(
          t('RISK-ANALYSIS_LICENSE-PLATE_CAR-BRAND-NOT-PERMITTED'),
        );
      setIsLicensePlateSupported(false);
    } else if (vehicle[0].car) {
      const [resp, status] = await request({ path: vehicle[0].self });
      if (status === 200) {
        const { car } = resp?.domain_car;
        handleChange(mapResponse(car, resp?.data?.images));
        nextSubView('vehicleData');
        setIsLicensePlateSupported(true);
      } else cogoToast.error(t('Could not retrieve vehicle info'));
    }
    setIsLoading(false);
  };

  const Question = () => (
    <QuestionGroupAssistantQuestion
      question={t('RISK-ANALYSIS_LICENSE-PLATE_PROVIDE-US-LICENSE')}
      description={t('RISK-ANALYSIS_LICENSE-PLATE_BY-ENTERING-YOUR-LICENSE')}
    />
  );

  return (
    <AdvisoryFlowMainContainer data-test-id="ProdFac_LicensePlateSearch">
      <Question />
      <QuestionGroupBody>
        <Box maxW="1024px" mx="auto">
          <Box
            mx="auto"
            w="fit-content"
            data-test-id="ProdFac_LicensePlateSearch_Container"
          >
            <LicensePlateLookup
              isLoading={isLoading}
              setIsLoading={(loading) => loading && setIsLoading(loading)}
              onChange={handleOnChange}
              onError={() => setIsLoading((prev) => prev && false)}
              initialValue={initialLicensePlate}
            />
          </Box>
          {brandNameFilter.length > 0 && (
            <Box mt="4">
              <Alert type={!isLicensePlateSupported ? 'error' : 'warning'}>
                {t('Only the following brands are permitted')}:
                <List
                  mt="2"
                  sx={{
                    '> li': {
                      listStyle: 'disc',
                      ml: '4',
                    },
                  }}
                >
                  {brandNameFilter.map((brand: string) => (
                    <li key={`list_item_${brand}`}>{brand}</li>
                  ))}
                </List>
              </Alert>
            </Box>
          )}
        </Box>
      </QuestionGroupBody>

      <QuestionGroupActions onBack={previousSubView} hideContinueButton />
    </AdvisoryFlowMainContainer>
  );
};

export default LicensePlateSearch;
